import { useState, useRef, useMemo } from 'react'
import { Upload, Loader2, X, Check, AlertTriangle, Trash2, Sparkles, ImageIcon } from 'lucide-react'
import { extractMenu, generateDishImage, uploadDishImage } from '../../lib/menuImport'

const ACCEPT = ['image/jpeg', 'image/png', 'image/webp']

// Id unique garanti (évite qu'une photo s'applique à plusieurs plats)
let uidCounter = 0
const uid = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return `id-${cryptoApi.randomUUID()}`
  uidCounter += 1
  return `id-${Date.now()}-${uidCounter}-${Math.random().toString(36).slice(2, 10)}`
}

function withUniqueIds(result) {
  return {
    ...result,
    categories: (result.categories || []).map((c) => ({
      ...c,
      id: uid(),
      items: (c.items || []).map((it) => ({ ...it, id: uid() })),
    })),
  }
}

export default function MenuImportWizard({ onDone, onCancel }) {
  const inputRef = useRef(null)
  const [files, setFiles] = useState([])
  const [step, setStep] = useState('upload') // upload | loading | review
  const [error, setError] = useState('')
  const [data, setData] = useState(null)
  const [generating, setGenerating] = useState({}) // { [itemId]: true }
  const [bulkRunning, setBulkRunning] = useState(false)

  const previews = useMemo(
    () => files.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })),
    [files]
  )

  function addFiles(list) {
    const valid = Array.from(list).filter((f) => ACCEPT.includes(f.type))
    if (valid.length < list.length) {
      setError('Seuls JPG, PNG et WEBP sont acceptés (pas de PDF).')
    } else {
      setError('')
    }
    setFiles((prev) => [...prev, ...valid].slice(0, 10))
  }

  async function analyze() {
    setStep('loading')
    setError('')
    try {
      const result = await extractMenu(files)
      setData(withUniqueIds(result))
      setStep('review')
    } catch (e) {
      setError(e.message)
      setStep('upload')
    }
  }

  function updateCat(ci, patch) {
    setData((d) => ({
      ...d,
      categories: d.categories.map((c, i) => (i === ci ? { ...c, ...patch } : c)),
    }))
  }

  // Mise à jour par id (sûre même si l'utilisateur supprime des plats pendant la génération)
  function patchItemById(itemId, patch) {
    setData((d) => ({
      ...d,
      categories: d.categories.map((c) => ({
        ...c,
        items: c.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)),
      })),
    }))
  }

  function updateItem(ci, ii, patch) {
    setData((d) => ({
      ...d,
      categories: d.categories.map((c, i) =>
        i !== ci
          ? c
          : { ...c, items: c.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) }
      ),
    }))
  }
  function removeItem(ci, ii) {
    setData((d) => ({
      ...d,
      categories: d.categories.map((c, i) =>
        i !== ci ? c : { ...c, items: c.items.filter((_, j) => j !== ii) }
      ),
    }))
  }

  async function generateOne(item, categoryName) {
    if (!item.name?.trim()) return
    setGenerating((g) => ({ ...g, [item.id]: true }))
    try {
      const url = await generateDishImage({
        name: item.name,
        description: item.description,
        category: categoryName,
      })
      patchItemById(item.id, { image: url })
    } catch (e) {
      setError(`Photo « ${item.name} » : ${e.message}`)
    } finally {
      setGenerating((g) => {
        const { [item.id]: _removed, ...rest } = g
        return rest
      })
    }
  }

  async function uploadOne(item, file) {
    if (!file) return
    if (!ACCEPT.includes(file.type)) {
      setError('Photo : seuls JPG, PNG et WEBP sont acceptés.')
      return
    }
    setGenerating((g) => ({ ...g, [item.id]: true }))
    try {
      const url = await uploadDishImage(file)
      patchItemById(item.id, { image: url })
    } catch (e) {
      setError(`Photo « ${item.name} » : ${e.message}`)
    } finally {
      setGenerating((g) => {
        const { [item.id]: _removed, ...rest } = g
        return rest
      })
    }
  }

  // Génère les photos manquantes, 2 à la fois pour éviter les 429
  async function generateAll() {
    if (!data) return
    setBulkRunning(true)
    setError('')
    const queue = data.categories.flatMap((c) =>
      c.items.filter((it) => !it.image && it.name?.trim()).map((it) => ({ it, cat: c.name }))
    )
    const workers = Array.from({ length: 2 }, async () => {
      while (queue.length) {
        const next = queue.shift()
        if (next) await generateOne(next.it, next.cat)
      }
    })
    await Promise.all(workers)
    setBulkRunning(false)
  }

  function confirm() {
    const categories = data.categories.map((c) => ({
      ...c,
      items: c.items.map(({ uncertain, ...it }) => it),
    }))
    onDone({ restaurant: data.restaurant, categories })
  }

  const uncertainCount =
    data?.categories.reduce((n, c) => n + c.items.filter((i) => i.uncertain).length, 0) ?? 0
  const missingImages =
    data?.categories.reduce((n, c) => n + c.items.filter((i) => !i.image).length, 0) ?? 0
  const anyGenerating = bulkRunning || Object.keys(generating).length > 0

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-xl">
        <header className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-slate-800">Importer un menu avec l'IA</h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 text-red-700 text-sm p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'upload' && (
            <>
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  addFiles(e.dataTransfer.files)
                }}
                className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-[#006b5f] transition"
              >
                <Upload className="w-8 h-8 mx-auto text-slate-400" />
                <p className="mt-2 text-sm text-slate-600">
                  Glissez vos photos de menu ici ou cliquez pour choisir
                </p>
                <p className="text-xs text-slate-400 mt-1">JPG, PNG, WEBP — 10 images max</p>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept={ACCEPT.join(',')}
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files)
                    e.target.value = ''
                  }}
                />
              </div>

              {previews.length > 0 && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {previews.map((p, i) => (
                    <div key={p.url} className="relative aspect-square rounded-lg overflow-hidden border">
                      <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                      <button
                        onClick={() => setFiles((f) => f.filter((_, j) => j !== i))}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {step === 'loading' && (
            <div className="py-16 flex flex-col items-center gap-3 text-slate-600">
              <Loader2 className="w-8 h-8 animate-spin text-[#006b5f]" />
              <p className="text-sm">Lecture du menu en cours… (10 à 40 secondes)</p>
            </div>
          )}

          {step === 'review' && data && (
            <div className="space-y-6">
              {uncertainCount > 0 && (
                <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                  {uncertainCount} plat(s) à vérifier (surlignés en orange).
                </p>
              )}

              <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 p-3">
                <p className="text-sm text-slate-600">
                  {missingImages > 0
                    ? `${missingImages} plat(s) sans photo`
                    : 'Tous les plats ont une photo'}
                </p>
                <button
                  onClick={generateAll}
                  disabled={anyGenerating || missingImages === 0}
                  className="px-3 py-1.5 text-sm rounded-lg bg-[#006b5f] text-white disabled:opacity-40 flex items-center gap-1.5"
                >
                  {bulkRunning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Générer toutes les photos
                </button>
              </div>

              {data.categories.map((cat, ci) => (
                <section key={cat.id} className="border rounded-xl p-4">
                  <input
                    value={cat.name}
                    onChange={(e) => updateCat(ci, { name: e.target.value })}
                    className="w-full font-semibold text-slate-800 border-b pb-1 mb-3 outline-none focus:border-[#006b5f]"
                  />
                  <div className="space-y-2">
                    {cat.items.map((it, ii) => (
                      <div
                        key={it.id}
                        className={`flex gap-3 items-start rounded-lg p-2 ${
                          it.uncertain ? 'bg-amber-50 ring-1 ring-amber-300' : 'bg-slate-50'
                        }`}
                      >
                        {/* Photo du plat */}
                        <div className="relative w-16 h-16 shrink-0 rounded-lg overflow-hidden border bg-white">
                          {it.image ? (
                            <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                              <ImageIcon className="w-6 h-6" />
                            </div>
                          )}
                          {generating[it.id] && (
                            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                              <Loader2 className="w-5 h-5 animate-spin text-[#006b5f]" />
                            </div>
                          )}
                          <button
                            onClick={() => generateOne(it, cat.name)}
                            disabled={!!generating[it.id] || !it.name?.trim()}
                            title={it.image ? 'Régénérer la photo' : 'Générer la photo'}
                            className="absolute bottom-0.5 right-0.5 bg-[#006b5f] text-white rounded-full p-1 disabled:opacity-40"
                          >
                            <Sparkles className="w-3 h-3" />
                          </button>
                          <label
                            title="Ajouter ma photo"
                            className={`absolute bottom-0.5 left-0.5 bg-slate-700 text-white rounded-full p-1 cursor-pointer ${
                              generating[it.id] ? 'opacity-40 pointer-events-none' : ''
                            }`}
                          >
                            <Upload className="w-3 h-3" />
                            <input
                              type="file"
                              accept={ACCEPT.join(',')}
                              className="hidden"
                              onChange={(e) => {
                                uploadOne(it, e.target.files?.[0])
                                e.target.value = ''
                              }}
                            />
                          </label>
                          {it.image && (
                            <button
                              onClick={() => patchItemById(it.id, { image: '' })}
                              title="Retirer la photo"
                              className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>

                        <div className="flex-1 grid grid-cols-12 gap-2 items-start">
                          <input
                            value={it.name}
                            onChange={(e) => updateItem(ci, ii, { name: e.target.value, uncertain: false })}
                            placeholder="Nom"
                            className="col-span-4 text-sm rounded border px-2 py-1"
                          />
                          <input
                            value={it.description}
                            onChange={(e) => updateItem(ci, ii, { description: e.target.value })}
                            placeholder="Description"
                            className="col-span-5 text-sm rounded border px-2 py-1"
                          />
                          <input
                            value={it.price}
                            onChange={(e) => updateItem(ci, ii, { price: e.target.value, uncertain: false })}
                            placeholder="Prix"
                            className="col-span-2 text-sm rounded border px-2 py-1"
                          />
                          <button
                            onClick={() => removeItem(ci, ii)}
                            className="col-span-1 text-slate-400 hover:text-red-600 pt-1.5"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 px-6 py-4 border-t">
          <button onClick={onCancel} className="px-4 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100">
            Annuler
          </button>
          {step === 'upload' && (
            <button
              disabled={!files.length}
              onClick={analyze}
              className="px-4 py-2 text-sm rounded-lg bg-[#006b5f] text-white disabled:opacity-40"
            >
              Analyser {files.length ? `(${files.length})` : ''}
            </button>
          )}
          {step === 'review' && (
            <button
              onClick={confirm}
              disabled={anyGenerating}
              className="px-4 py-2 text-sm rounded-lg bg-[#006b5f] text-white flex items-center gap-1 disabled:opacity-40"
            >
              <Check className="w-4 h-4" /> Utiliser ce menu
            </button>
          )}
        </footer>
      </div>
    </div>
  )
}